import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import {DocumentNode} from 'graphql';
import gql from 'graphql-tag';
import React from 'react';

import ExtractByTypename from 'graphql-client/ExtractByTypename';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHTER} from 'ui-library/Colors';
import Modal from 'ui-library/modals/Modal';
import runModal from 'ui-library/runModal';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import copyToClipboard from 'util/clipboard/copyToClipboard';

import {ClientCredentialSectionCredential} from './__generated/ClientCredentialSectionCredential';

export type ClientCredentialSectionSecrets = ExtractByTypename<
  ClientCredentialSectionCredential['oauthCredential'],
  'OAuthCredentialNode'
>['clientSecrets'][number];

export const CLIENT_CREDENTIAL_SECTION_CREDENTIAL_FRAGMENT: DocumentNode = gql`
  fragment ClientCredentialSectionCredential on BenchlingAppNode {
    id
    oauthCredential {
      ... on InaccessibleInfoNode {
        invalidId
      }
      ... on OAuthCredentialNode {
        clientId
        clientSecrets {
          suffix
          createdAt
        }
      }
    }
  }
`;

interface ClientSecretTableProps {
  clientSecrets: Array<{
    suffix: string;
  }>;
  clientId: string;
  appName: string;
  title: string;
  onDelete: (id?: number) => Promise<void>;
  onRegenerate: (id?: number) => Promise<void>;
  isGlobal: boolean;
  dataTestAttrs?: DataTestAttrs;
}

export function ClientSecretTable(props: ClientSecretTableProps): JSX.Element {
  const regenerateSecret = async (): Promise<void> => {
    await props.onRegenerate();
  };
  const deleteSecret = async (): Promise<void> => {
    await props.onDelete();
  };
  const appHasCredentials = props.clientSecrets.length !== 0;
  const shouldEnableRollingCredentials = true;

  return (
    <div
      className={classNames('u-spacing-top container', css(styles.tableWrapper))}
      {...getTestProps(props.dataTestAttrs)}
    >
      <Row>
        <Col md={8} className={css(styles.pullLeft)}>
          <strong>Client ID</strong>
        </Col>
      </Row>
      <Row>
        <Col md={6} className={classNames('u-width-full', css(styles.grayBox))}>
          <div>
            <span {...getTestProps({component: 'ClientSecretTable', element: 'clientId'})}>
              {props.clientId}
            </span>
            <div className="pull-right">
              <Tooltip container="body" placement={TooltipPlacement.TOP} title="Copy Client ID Key">
                <Button
                  buttonStyle={ButtonStyle.CIRCLE_ICON}
                  size={ButtonSize.MINI}
                  className={css(styles.noRightPadding)}
                  icon="copy"
                  onClick={() =>
                    copyToClipboard(props.clientId, {
                      showToastOnSuccess: true,
                      toastText: 'Copied Client ID to clipboard.',
                    })
                  }
                />
              </Tooltip>
            </div>
          </div>
        </Col>
      </Row>
      {shouldEnableRollingCredentials && (
        <Row>
          <Col md={0} className={css(styles.noRightPadding)}>
            <Button
              buttonStyle={ButtonStyle.SECONDARY_WHITE}
              size={ButtonSize.SMALL}
              onClick={
                !appHasCredentials
                  ? regenerateSecret
                  : () =>
                      runModal(ConfirmRegenerateClientSecretModal, {
                        handleRegenerateSecret: regenerateSecret,
                        appName: props.appName,
                      })
              }
              className="pull-right"
              text={appHasCredentials ? 'Regenerate Secret' : 'Generate Secret'}
              dataTestAttrs={{component: 'ClientSecretTable', element: 'generateSecretButton'}}
            />
          </Col>
        </Row>
      )}
      <Row>
        <Table
          numRows={props.clientSecrets.length}
          overflowVisible={true}
          emptyTableText="No associated client secrets"
        >
          <Column
            header="Client Secret"
            renderCell={(rowIndex: number) => (
              <Cell>
                <div className={css(styles.secretContainer)}>
                  <span>**********{props.clientSecrets[rowIndex].suffix}</span>
                </div>
                <Tooltip container="body" placement={TooltipPlacement.TOP} title="Delete Client Secret">
                  <Button
                    buttonStyle={ButtonStyle.CIRCLE_ICON}
                    size={ButtonSize.SMALL}
                    className="u-spacing-left"
                    icon="delete"
                    onClick={() => {
                      runModal(ConfirmDeleteClientSecretModal, {
                        appName: props.appName,
                        handleDelete: deleteSecret,
                      });
                    }}
                  />
                </Tooltip>
              </Cell>
            )}
          />
        </Table>
      </Row>
    </div>
  );
}

export function ConfirmDeleteClientSecretModal({
  handleDelete,
  appName,
  onRequestClose,
  onRequestSubmit,
}: {
  handleDelete: (id?: number) => void;
  appName: string;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}): JSX.Element {
  function handleSubmit(): void {
    handleDelete();
    onRequestSubmit();
  }
  return (
    <Modal
      onRequestClose={onRequestClose}
      onRequestSubmit={handleSubmit}
      title="Confirm Client Secret Deletion"
      buttons={[{text: 'Delete Client Secret', submit: true, style: ButtonStyle.NEGATIVE}]}
    >
      Confirm you’d like to remove <b>{appName}</b>'s Client Secret. Access for any future requests using this
      Client Secret will be revoked.
    </Modal>
  );
}

export function ConfirmRegenerateClientSecretModal({
  handleRegenerateSecret,
  appName,
  onRequestClose,
  onRequestSubmit,
}: {
  handleRegenerateSecret: (id?: number) => Promise<void>;
  appName: string;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}): JSX.Element {
  async function handleSubmit(): Promise<void> {
    await handleRegenerateSecret();
    onRequestSubmit();
  }
  return (
    <Modal
      onRequestClose={onRequestClose}
      onRequestSubmit={handleSubmit}
      title="Confirm Client Secret Regeneration"
      buttons={[{text: 'Regenerate Client Secret', submit: true, style: ButtonStyle.SECONDARY}]}
    >
      Generating a new Client Secret will delete <b>{appName}</b>'s current one.
    </Modal>
  );
}

export default function ClientCredentialSection({
  clientSecrets,
  appName,
  clientId,
  handleRegenerateSecret,
  handleDelete,
  isGlobal,
}: {
  clientSecrets: Array<ClientCredentialSectionSecrets>;
  clientId: string;
  appName: string;
  handleRegenerateSecret: (id?: number) => Promise<void>;
  handleDelete: (id?: number) => Promise<void>;
  isGlobal: boolean;
}): JSX.Element {
  return (
    <ClientSecretTable
      appName={appName}
      title="Client Secrets"
      clientSecrets={clientSecrets}
      onDelete={handleDelete}
      onRegenerate={handleRegenerateSecret}
      clientId={clientId}
      isGlobal={isGlobal}
      dataTestAttrs={{component: 'ClientCredentialSection', element: 'client'}}
    />
  );
}

const styles = StyleSheet.create({
  secretContainer: {
    display: 'inline-block',
    width: 'calc(100% - 50px)',
  },
  secret: {
    fontSize: 20,
  },
  pullLeft: {
    paddingLeft: 0,
  },
  tableWrapper: {
    marginLeft: 0,
  },
  noRightPadding: {
    paddingRight: 0,
  },
  grayBox: {
    backgroundColor: GRAY_LIGHTER,
    padding: 10,
  },
});
